import { useMemo, useRef, useEffect } from "react";
import { YStack, XStack, Input, View } from "tamagui";
import { colors } from "@styles/colors";

interface OTPInputBlocksProps {
  value: string;
  onChange: (val: string) => void;
  onComplete?: (val: string) => void;
  error?: string;
  digits?: number;
  /**
   * 預設 false → 僅接受數字（手機 OTP、客戶序號）。
   * true → 接受家庭邀請碼字元集（A-Z 2-9，自動轉大寫、去除易混淆字元），
   * 對應後端 JoinContextDto 的 `^[A-Z2-9]{6}$`。
   */
  alphanumeric?: boolean;
}

/** 依模式淨化輸入：數字模式留 0-9；邀請碼模式轉大寫後留 A-Z2-9（去除 0/1/O/I）。 */
const sanitize = (text: string, alphanumeric: boolean): string =>
  alphanumeric
    ? text.toUpperCase().replace(/[^A-Z2-9]/g, "")
    : text.replace(/[^0-9]/g, "");

export const OTPInputBlocks = ({
  value,
  onChange,
  onComplete,
  error,
  digits = 5,
  alphanumeric = false,
}: OTPInputBlocksProps) => {
  const otpArray = useMemo(() => {
    const arr = (value || "").split("");
    while (arr.length < digits) arr.push("");
    return arr.slice(0, digits);
  }, [value, digits]);

  const inputRefs = useRef<(any | null)[]>([]);
  const prevLenRef = useRef((value || "").length);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // 驗證失敗時，外部常會把已填滿的驗證碼清空——清空後重新 focus「第一格」，讓使用者可直接重打。
  // 用 setTimeout 延遲：贏過清空後的 re-render 與 react-hook-form setError 可能造成的搶焦點。
  useEffect(() => {
    const prev = prevLenRef.current;
    const curr = (value || "").length;
    prevLenRef.current = curr;
    if (prev > 0 && curr === 0) {
      const t = setTimeout(() => inputRefs.current[0]?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [value, digits]);

  const handleChange = (text: string, index: number) => {
    const digitsOnly = sanitize(text, alphanumeric);

    // Multi-char input — iOS QuickType bar SMS autofill 會把整段驗證碼塞進
    // focused input（繞過 maxLength）。也涵蓋桌面 Cmd+V 沒觸發 onPaste 的
    // edge case。從 index 開始往後分配到各 box、focus 最後一個填到的位置。
    if (digitsOnly.length > 1) {
      const newOtpArr = [...otpArray];
      const remaining = digits - index;
      const toFill = digitsOnly.slice(0, remaining).split("");
      toFill.forEach((d, i) => {
        newOtpArr[index + i] = d;
      });
      const newVal = newOtpArr.join("");
      onChange(newVal);

      const lastIdx = Math.min(index + toFill.length - 1, digits - 1);
      inputRefs.current[lastIdx]?.focus();

      if (newVal.length === digits && !newOtpArr.includes("")) {
        onComplete?.(newVal);
      }
      return;
    }

    // Single-char input — typing
    const char = digitsOnly.slice(-1);
    if (text && !char) return;

    const newOtpArr = [...otpArray];
    newOtpArr[index] = char;
    const newVal = newOtpArr.join("");
    onChange(newVal);
    if (char) {
      if (index < digits - 1) {
        inputRefs.current[index + 1]?.focus();
      } else if (index === digits - 1 && newVal.length === digits) {
        onComplete?.(newVal);
      }
    }
  };

  const handleKeyPress = ({ nativeEvent }: any, index: number) => {
    if (nativeEvent.key === "Backspace") {
      if (!otpArray[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        const newOtpArr = [...otpArray];
        newOtpArr[index - 1] = "";
        onChange(newOtpArr.join(""));
      }
    }
  };

  const handlePaste = (e: any) => {
    e.preventDefault();
    const pasteData = (e.clipboardData || (window as any).clipboardData).getData("text");
    const pasteDigits = sanitize(pasteData, alphanumeric).slice(0, digits).split("");
    const newOtpArr = Array(digits).fill("");
    pasteDigits.forEach((d: string, i: number) => {
      newOtpArr[i] = d;
    });
    const newVal = newOtpArr.join("");
    onChange(newVal);
    
    // Focus last filled or next empty
    const focusIndex = Math.min(pasteDigits.length, digits - 1);
    inputRefs.current[focusIndex]?.focus();
    
    if (newVal.length === digits) {
      onComplete?.(newVal);
    }
  };

  return (
    <YStack gap="$2" width="100%" maxWidth={400} alignSelf="center">
      <XStack gap="2.5%" justifyContent="center" width="100%">
        {otpArray.map((char, index) => {
          const InputAny = Input as any;
          // Calculate dynamic size: (100% - total gaps) / number of digits
          // We use aspect-ratio to keep it square or slightly tall
          return (
            <View key={index} f={1} style={{ aspectRatio: "1/1.2" } as any} maxWidth={60}>
              <InputAny
                ref={(el: any) => (inputRefs.current[index] = el)}
                width="100%"
                height="100%"
                p={0}
                fontSize={digits > 5 ? 20 : 22}
                fontWeight="bold"
                textAlign="center"
                keyboardType={alphanumeric ? "default" : "number-pad"}
                // iOS QuickType bar SMS OTP autofill 要靠這三個屬性才會出現
                // 「自動帶入」chip：autoComplete="one-time-code" 是 Safari /
                // iOS WebKit 的官方 hint；inputMode="numeric" 帶數字鍵盤；
                // name="otp" 給 autofill heuristic 多一條線索。
                // 邀請碼為英數混合：關閉 OTP autofill、改文字鍵盤並強制大寫。
                autoComplete={alphanumeric ? "off" : "one-time-code"}
                inputMode={alphanumeric ? "text" : "numeric"}
                autoCapitalize={alphanumeric ? "characters" : undefined}
                name={`otp-${index}`}
                value={char}
                onChangeText={(text: string) => handleChange(text, index)}
                onKeyPress={(e: any) => handleKeyPress(e, index)}
                onPaste={index === 0 ? handlePaste : undefined}
                maxLength={1}
                borderColor={error ? "$red10" : colors.primary}
                borderWidth={1.5}
                borderRadius={10}
                backgroundColor="white"
                focusStyle={{ borderColor: colors.primary, borderWidth: 2 }}
              />
            </View>
          );
        })}
      </XStack>
    </YStack>
  );
  };
